import { Option } from '@editor/components/Dropdown/types';

import { DownloadFormat } from '../stores/downloadStore/types';
import {
  BleedDefinition,
  BleedOption,
  BlendMode,
  BoardLayout,
  ObjectType,
  Unit,
} from '../types';

export const UNTITLED_PROJECT_NAME = 'Untitled Project';

export const MAX_FONT_SIZE = 3000;
export const SHOW_ONBOARDING_VIDEO_POPUP_LOCAL_STORAGE_KEY =
  'htcShowOnboardingVideoPopup';

// Moved from src/fabric/index.js
export const GROUP_TYPES = [ObjectType.Group, ObjectType.ActiveSelection];
// Moved from src/fabric/controls/danger.js
export const ALERT_WIDTH = 24;

// key for device id
export const DEVICE_ID_KEY = 'htcDeviceId';
// key for user onboarding
export const ONBOARDING_KEY = 'htcOnboarding';
// key for artboard settings
export const ARTBOARD_SETTINGS_KEY = 'htcArtboardSettings';
/**
 * key for current state
 * @deprecated we now only want to store PROJECT_ID_KEY
 */
export const CANVAS_STATE_KEY = 'artboard';
// key for current projectId
export const PROJECT_ID_KEY = 'htcProjectId';

//name of cookie to use in website to check if editor has been visited
export const HTC_DESIGNER_VISITED = 'htcDesignerVisited';

export const HIGH_RES_WARNING_DISMISSED = 'htcHighResWarningDismissed';
export const QUIET_MODE_KEY = 'htcQuietMode';

// cookie storing impact.com referral id
export const IMPACT_CLICK_ID = 'ktlImpactClickId';

// URL parameter for ad click id
export const URLPARAM_ADCLICKID_LIST = ['gclid', 'fbclid'].concat(
  process.env.URLPARAM_ADCLICKID_LIST?.split(',') || []
);

// cookie storing ad click id, order matters
export const COOKIE_ADCLICKID_ORDERED_LIST =
  process.env.REACT_APP_COOKIE_ADCLICKID_ORDERED_LIST?.split(',') || [
    '_gcl_aw',
    '_gcl_dc',
    '_fbc',
  ];

export const COOKIE_UTM_DATA = 'htcUtmData';

export const COOKIE_LICENSING_MODAL_DISMISSED = 'htcLicensingModalDismissed';

export const COOKIE_AUTH_REDIRECT_KEY = 'htcAuthRedirectKey';

export const DPI_72 = 72;
export const DPI_150 = 150;
export const DPI_300 = 300;
export const DEFAULT_DPI = DPI_72;

export const ONE_MM_IN_INCHES = 5 / 127;
export const ARTBOARD_DPI = 60;
export const DEPRECATED_DPMM = 3;
export const MAX_ARTBOARD_SIZE = 10800;
export const MIN_ARTBOARD_SIZE = 60;
const MAX_FREE_EXPORT_SCALE = 800 / MAX_ARTBOARD_SIZE; // max pixel goes to 800

export const MAXIMUM_SIZE = {
  mm: MAX_ARTBOARD_SIZE / (ARTBOARD_DPI * ONE_MM_IN_INCHES),
  in: MAX_ARTBOARD_SIZE / ARTBOARD_DPI,
  px: MAX_ARTBOARD_SIZE,
};

const MAX_OBJECT_INPUT_SIZE = MAX_ARTBOARD_SIZE * 10;
export const MAX_OBJECT_INPUT_SIZES = {
  mm: MAX_OBJECT_INPUT_SIZE / (ARTBOARD_DPI * ONE_MM_IN_INCHES),
  in: MAX_OBJECT_INPUT_SIZE / ARTBOARD_DPI,
  px: MAX_OBJECT_INPUT_SIZE,
};

export const MAX_EXPORT_SIZE = 10800;
export const MAXIMUM_EXPORT_SIZE = {
  mm: MAX_EXPORT_SIZE / (ARTBOARD_DPI * ONE_MM_IN_INCHES),
  in: MAX_EXPORT_SIZE / ARTBOARD_DPI,
  px: MAX_EXPORT_SIZE,
};

export const MAXIMUM_FREE_EXPORT_SIZE = {
  mm: MAXIMUM_SIZE.mm * MAX_FREE_EXPORT_SCALE,
  in: MAXIMUM_SIZE.in * MAX_FREE_EXPORT_SCALE,
  px: MAXIMUM_SIZE.px * MAX_FREE_EXPORT_SCALE,
};

export const MINIMUM_SIZE = {
  mm: MIN_ARTBOARD_SIZE / (ARTBOARD_DPI * ONE_MM_IN_INCHES),
  in: MIN_ARTBOARD_SIZE / ARTBOARD_DPI,
  px: MIN_ARTBOARD_SIZE,
};

export const MINIMUM_EXPORT_SIZE = MINIMUM_SIZE;

export const TABLET_SIZE_LIMIT = 4096;
export const MAXIMUM_TABLET_EXPORT_SIZE = {
  mm: TABLET_SIZE_LIMIT / (ARTBOARD_DPI * ONE_MM_IN_INCHES),
  in: TABLET_SIZE_LIMIT / ARTBOARD_DPI,
  px: TABLET_SIZE_LIMIT,
};

export const UNITS: Record<Unit, string> = {
  // id: label
  in: '"',
  px: 'px',
  mm: 'mm',
};

export const UNIT_OPTIONS: { id: Unit; label: string }[] = [
  { id: Unit.in, label: 'Inches (in)' },
  { id: Unit.px, label: 'Pixels (px)' },
  { id: Unit.mm, label: 'Millimetres (mm)' },
] as const;

export const SHORT_UNIT_OPTIONS: { id: Unit; label: string }[] = [
  { id: Unit.in, label: 'inch' },
  { id: Unit.px, label: 'px' },
  { id: Unit.mm, label: 'mm' },
] as const;

// designs
export const AUTOSAVE_THROTTLE_DELAY = 15 * 1000; // 15 seconds
export const MAX_DESIGN_NAME_LENGTH = 48;

export const DEFAULT_ZOOM = 0.8;

export const defaultBoardFill = '#FFFFFF';

export const defaultBoardOptions = {
  activeLayout: BoardLayout.Standard,
  width: 1200,
  height: 1200,
  fill: defaultBoardFill,
  opacity: 1,
  dpi: DEFAULT_DPI,
  clip: true,
  presets: [],
};

export type SettingsType = {
  gridSize: number;
  showGrid: boolean;
  showRuler: boolean;
  showPrintMargins: boolean;
  enableGuideRedraw: boolean;
};

export const defaultSettings: SettingsType = {
  gridSize: 100,
  showGrid: false,
  showRuler: false,
  showPrintMargins: false,
  enableGuideRedraw: true,
};

export const SOCIAL_SHARE_TARGET_RESOLUTION = 1200;

export const DEFAULT_SORT = 'popularity';
export const DEFAULT_SORT_ALL = 'recent';
export const DEFAULT_TAKE = 10;
export const DEFAULT_WORKSPACE_FONTS_TAKE = 50;

export const MIN_PREVIEW_SIZE = 1024;
export const MIN_PROJECT_PREVIEW_SIZE = 724; // largest size we use to load design previews on website

export const DEFAULT_OVERLAY_MODE: Option = {
  id: BlendMode.Normal,
  label: 'Normal',
  default: true,
};

// svg blend modes: https://www.w3.org/TR/SVG11/filters.html#feBlendModeAttribute (https://drafts.fxtf.org/compositing-2/#ltblendmodegt)
// canvas composition operations https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
// other available modes: 'screen', 'darken', 'lighten', 'color-dodge', 'hard-light', 'difference', 'exclusion', 'hue', 'saturation', 'color', 'luminosity',
// Checkout all the modes available on fabricjs: https://github.com/fabricjs/fabric.js/blob/master/test/visual/globalCompositeOperation.js#L8
export const overlayModes: Option[] = [
  // Normal
  DEFAULT_OVERLAY_MODE,

  // Standard
  { id: 'standart-divider', label: 'Divider', category: 'Divider' },
  { id: 'standard', label: 'Standard', category: 'GroupTitle' },
  { id: BlendMode.Multiply, label: 'Multiply' },
  { id: BlendMode.Screen, label: 'Screen' },
  { id: BlendMode.Overlay, label: 'Overlay' },

  // Lighting and shadows
  { id: 'lighting-and-shadow-divider', label: 'Divider', category: 'Divider' },
  {
    id: 'lighting-and-shadows',
    label: 'Lighting & Shadows',
    category: 'GroupTitle',
  },
  { id: BlendMode.SoftLight, label: 'Soft Light' },
  { id: BlendMode.HardLight, label: 'Hard Light' },
  { id: BlendMode.ColorDodge, label: 'Color dodge' },
  { id: BlendMode.ColorBurn, label: 'Color Burn' },

  // Contrast
  { id: 'contrast-divider', label: 'Contrast', category: 'Divider' },
  { id: 'contrast', label: 'Contrast', category: 'GroupTitle' },
  { id: BlendMode.Lighten, label: 'Lighten' },
  { id: BlendMode.Darken, label: 'Darken' },
  { id: BlendMode.Lighter, label: 'Lighter Color' },
  { id: BlendMode.Difference, label: 'Difference' },
  { id: BlendMode.Exclusion, label: 'Exclusion' },

  // Color Properties
  {
    id: 'color-properties-divider',
    label: 'Color Properties',
    category: 'Divider',
  },
  {
    id: 'color properties',
    label: 'Color Properties',
    category: 'GroupTitle',
  },
  { id: BlendMode.Hue, label: 'Hue' },
  { id: BlendMode.Saturation, label: 'Saturation' },
  { id: BlendMode.Color, label: 'Color' },
  { id: BlendMode.Luminosity, label: 'Luminosity' },

  // Special
  { id: 'special-divider', label: 'Special', category: 'Divider' },
  { id: 'special', label: 'Special', category: 'GroupTitle' },
  { id: BlendMode.AlphaMask, label: 'Alpha Mask' },
];

export const SMALL_TAKE = 3;

/** @knipignore */
export const ENABLE_BILLING =
  (process.env.REACT_APP_ENABLE_BILLING ||
    process.env.NEXT_PUBLIC_ENABLE_BILLING) === 'true';

export const REACT_APP_GROWTHBOOK_SDK_API_KEY =
  process.env.REACT_APP_GROWTHBOOK_SDK_API_KEY ||
  process.env.NEXT_PUBLIC_GROWTHBOOK_SDK_API_KEY;

export const COLOR_PICKER_URL = '/icons/eyedropper.svg';

export const MOCKUP_TARGET_SIZE = 3000;

export const MOCKUP_EXPORT_SIZE_FREE = {
  width: 800,
  height: 800,
  unit: Unit.px,
};

export const MOCKUP_EXPORT_SIZE_PRO = {
  width: 3000,
  height: 3000,
  unit: Unit.px,
};

export const MOCKUP_EXPORT_SIZE_EXPERT = {
  width: 3000,
  height: 3000,
  unit: Unit.px,
};

// Export size for mockups is fixed to 3000 x 3000
export const MOCKUP_EXPORT_OPTIONS = {
  format: DownloadFormat.jpg,
  dpi: DEFAULT_DPI,
};

export const MAX_FILE_UPLOAD_COUNT = 100;
export const MAX_FILE_UPLOAD_PIXEL_SIZE = 10800;

export const AI_ACTION_KEY = 'aiImageGenerated';

export const ACCEPTED_TERMS_ACTION_KEY = 'acceptedTerms202305';

export const VECTORIZING_MAX_PIXEL_COUNT = 1024 ** 2;

export const DESIGN_LISTING_UPDATE_INTERVAL = 15_000;

export const FREE_TRIAL_DAYS_AMOUNT = Number(
  process.env.REACT_APP_FREE_TRIAL_DAYS_AMOUNT ||
    process.env.NEXT_PUBLIC_FREE_TRIAL_DAYS_AMOUNT ||
    7
);

export const TRIAL_EXPIRE_NOTIFICATION_DAYS = Number(
  process.env.REACT_APP_TRIAL_EXPIRE_NOTIFICATION_DAYS ||
    process.env.NEXT_PUBLIC_TRIAL_EXPIRE_NOTIFICATION_DAYS ||
    3
);

export const EDITOR_AUTH_REDIRECT_KEY = 'editor';

export const HEADER_BANNER_HEIGHT = 40;
export const BOTTOM_MENU_AREA_HEIGHT = 70;

export const MAXIMAL_VIEWPORT_POSITION = 200_000;

export const ARTBOARD_SEPARATION_GAP = 200;

export const PRINT_MARGIN_DEFAULT_RATIO = 0.08; // 8%
export const PRINT_MARGIN_MAX_RATIO = 0.4; // 40%
export const PRINT_MARGIN_MIN_VALUE = 0;

export const PRINT_BLEED_DEFAULT: BleedDefinition = {
  value: 0.125,
  unit: Unit.in,
} as const;

export const PRINT_BLEED_AVAILABLE_OPTIONS: BleedOption[] = [
  { label: 'Standard size', ...PRINT_BLEED_DEFAULT },
] as const;

export const MIN_ERASER_SIZE = 1;

export const MAX_ERASER_SIZE = 200;

// Link to the article with AI feature Erase & Fill
export const ERASE_AND_FILL_BLOG_ARTICLE_URL =
  'https://www.kittl.com/article/kittl-ai-erase-and-fill';
export const AI_CREDIT_CHANGE_BLOG_ARTICLE_URL =
  'https://www.kittl.com/article/kittl-new-monthly-ai-credit-system';

export const VIDEO_HEIGHT_LARGE = 168;
export const VIDEO_HEIGHT_MEDIUM = 150;
export const STATIC_VIDEOS_PATH = 'https://static.kittl.com/videos';

export enum PERFORMANCE_METRIC {
  FRAME_TIME_RENDER_PER_SECOND_BOARDS = 'frame_time.render_per_second_boards',
  FRAME_TIME_RENDER_STANDALONE_OBJECTS = 'frame_time.render_standalone_objects',
  FRAME_TIME_DOM_CONTENT_LOAD_TIME = 'frame_time.dom_content_load_time',
  FRAME_TIME_DESIGN_PREVIEW_VISIBLE_TIME = 'frame_time.design_preview_visible_time',
  FRAME_TIME_PROJECT_LOAD_TIME = 'frame_time.project_load_time',
  NUMBER_OF_ARTBOARDS = 'number_of.artboards',
  SIZE_OF_STATE = 'size_of.state',
}

export const STATIC_YOUTUBE_VIDEO_URL = 'https://www.youtube.com/embed';
export const FREE_PLAN_MAX_MEMBER_COUNT = 5;

export const ASSET_SELECTION_COLOR = '#AB84FF';

export const DEFAULT_NEW_OBJECT_SCALE_FROM_SCREEN = 1 / 3;

export const MICRO_DRAG_THRESHOLD = 2.5;
export const LAYERS_LIMIT = 500;
/**
 * Object types that are allowed to be erased and vectorized, but
 * can't be erased on their own.
 */
export const VALID_TYPES_FOR_ERASE_AND_VECTORIZE = [
  ObjectType.Illustration,
  ObjectType.BasicShape,
  ObjectType.CombinedShape,
  ObjectType.PathText,
];

// The types of objects that can be replaced
export const REPLACEABLE_TYPES = [
  ObjectType.BasicShape,
  ObjectType.Illustration,
  ObjectType.IllustrationImage,
  ObjectType.CombinedShape,
];

export const GROWTHBOOK_LOCAL_OVERRIDES = process.env
  .REACT_APP_GROWTHBOOK_OVERRIDES
  ? (JSON.parse(process.env.REACT_APP_GROWTHBOOK_OVERRIDES) as Record<
      string,
      boolean
    >)
  : {};

export const TEMPLATE_ADDING_TOAST_DURATION = 1500;
export const TEMPLATE_ADDED_TOAST_DURATION = 7000;
export const EXPORT_COMPLETE_TOAST_DURATION = 7000;
